import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { Button, CheckboxOptionType, Radio, RadioChangeEvent, Space, Spin, Form} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import IDeal, {
    IDealErrors,
} from '@/Api/Deal/IDeal';
import LoanPurposeEnum from '@/Api/Deal/LoanPurposeEnum';
import {
    dealErrorSetAction,
    dealGetAction,
    dealValueSetAction,
} from '@/Lead/actions';
import {
    dealSelector,
    errorsSelector,
} from '@/Lead/selectors';
import IToken from '@/Auth/IToken';
import { Dispatch } from 'redux';
import LeftDrawerLayout from './LeftDrawerLayout';
import FormItem from '@/UI/FormItem';
import { decodedTokenSelector } from '@/Auth/selectors';
import { loanPurposeLabels } from '@/Borrow/loanPurposeLabels';
import { IDictionary } from '@/utilities/IDictionary';
import HouseExchange from '@/UI/HouseExchange';
import Reinvest from '@/UI/Reinvest';
import Bricks from '@/UI/Bricks';
import Renovation from '@/UI/Renovation';
import Suitcase from '@/UI/Suitcase';
import HeartSign from '@/UI/HeartSign';

const purposeOptions: IDictionary<JSX.Element> = {
    [LoanPurposeEnum.BridgingLoan]: <HouseExchange className='icon' />,
    [LoanPurposeEnum.Refinance]: <Reinvest className='icon' />,
    [LoanPurposeEnum.DevelopmentLoan]: <Bricks className='icon' />,
    [LoanPurposeEnum.RenovateOrBuild]: <Renovation className='icon' />,
    [LoanPurposeEnum.BusinessLoan]: <Suitcase className='icon' />,
    [LoanPurposeEnum.PersonalLoan]: <HeartSign className='icon' />,
};

export default function LoanPurpose(): ReactElement {
    const navigate: NavigateFunction = useNavigate();
    const token: IToken = useSelector(decodedTokenSelector);
    const deal: IDeal = useSelector(dealSelector);
    const errors: IDealErrors = useSelector(errorsSelector);
    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!deal.uuid) {
            dispatch(dealGetAction());
        }
    }, [
        token,
        deal,
        dispatch,
    ]);

    const validateLoanPurpose: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!_.values(LoanPurposeEnum).includes(deal.loanPurpose)) {
            error = 'Please select your loan purpose';
        }

        dispatch(dealErrorSetAction('loanPurpose', error));

        return !error;
    }, [
        deal,
        dispatch,
    ]);

    const onClickContinue: () => void = useCallback((): void => {
        if (!validateLoanPurpose()) {
            return;
        }

        navigate('/get-your-quote/properties');
    }, [
        navigate,
        validateLoanPurpose,
    ]);

    const onClickLoanPurpose: (e: RadioChangeEvent) => void = useCallback((e: RadioChangeEvent): void => {
        dispatch(dealValueSetAction('loanPurpose', e.target.value));
    }, [
        dispatch,
    ]);

    if (!deal.uuid) {
        return (
            <LeftDrawerLayout currentStep={3} pageName='loan-purpose'>
                <h3 className='headline'>How will you use the funds?</h3>
                <Spin indicator={<LoadingOutlined spin={true} />} />
            </LeftDrawerLayout>
        );
    }

    const options: CheckboxOptionType<LoanPurposeEnum>[] = _.keys(purposeOptions).map((loanPurpose: string): CheckboxOptionType<LoanPurposeEnum> => {
        return ({
            label: (
                <Space className='content' direction='vertical'>
                    {purposeOptions[loanPurpose]}
                    <span className='description'>{loanPurposeLabels[loanPurpose]}</span>
                </Space>
            ),
            value: loanPurpose as LoanPurposeEnum,
        });
    });

    return (
        <LeftDrawerLayout currentStep={3} pageName='loan-purpose'>
            <Helmet>
                <title>Funding.com.au</title>
                <meta content='Tell us what your loan is for. We have a range of options available to suit your needs.' name='description' />
            </Helmet>
            <h3 className='headline'>How will you use the funds?</h3>
            <p className='intro'>
                Funding can help with your bridging, business and building finance needs. Let us know what you&apos;re looking for.
            </p>
            <Form>
                <FormItem
                    className='loan-purpose'
                    help={errors.loanPurpose}
                    layout='vertical'
                    name='loanPurpose'
                    validateStatus={errors.loanPurpose ? 'error' : ''}
                >
                    <Radio.Group
                        buttonStyle='solid'
                        className='card-radio-group'
                        options={options}
                        value={deal.loanPurpose}
                        onChange={onClickLoanPurpose}
                    />
                </FormItem>
            </Form>
            <div className='actions'>
                <Link to='/get-your-quote/loan-amount'><Button className='previous' type='primary'>Back</Button></Link>
                <Button type='primary' onClick={onClickContinue}>Save and continue</Button>
            </div>
        </LeftDrawerLayout>
    );
}

